import { useState, useCallback, useRef, useEffect } from 'react';
import { CreateEmergencyAlertData, createEmergencyAlert } from '../api/guestExperience';

export interface QueuedAlert {
  id: string;
  data: CreateEmergencyAlertData;
  queuedAt: number;
}

interface UseOfflineAlertQueueResult {
  queueAlert: (data: CreateEmergencyAlertData) => void;
  pendingAlerts: QueuedAlert[];
  staleAlert: QueuedAlert | null;
  confirmStaleAlert: () => void;
  dismissStaleAlert: () => void;
}

const STALE_THRESHOLD_MS = 300000; // 5 minutes
const BASE_RETRY_MS = 1000;
const MAX_RETRY_MS = 30000;

export function useOfflineAlertQueue(eventId: string): UseOfflineAlertQueueResult {
  const [pendingAlerts, setPendingAlerts] = useState<QueuedAlert[]>([]);
  const [staleAlert, setStaleAlert] = useState<QueuedAlert | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const processingRef = useRef(false);
  const retryTimeoutRef = useRef<number | null>(null);
  const retryDelayRef = useRef(BASE_RETRY_MS);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const sendAlert = useCallback(
    async (alert: QueuedAlert): Promise<boolean> => {
      try {
        await createEmergencyAlert(eventId, alert.data);
        return true;
      } catch {
        return false;
      }
    },
    [eventId],
  );

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    setPendingAlerts((current) => {
      if (current.length === 0) {
        processingRef.current = false;
        return current;
      }

      const nextAlert = current[0];
      const age = Date.now() - nextAlert.queuedAt;

      // If alert is stale, prompt user for confirmation
      if (age > STALE_THRESHOLD_MS) {
        setStaleAlert(nextAlert);
        processingRef.current = false;
        return current;
      }

      // Attempt to send (async, outside setState)
      sendAlert(nextAlert).then((success) => {
        if (success) {
          retryDelayRef.current = BASE_RETRY_MS;
          setPendingAlerts((prev) => {
            const remaining = prev.filter((a) => a.id !== nextAlert.id);
            processingRef.current = false;
            // Continue processing remaining alerts
            if (remaining.length > 0 && navigator.onLine) {
              scheduleRetry(BASE_RETRY_MS);
            }
            return remaining;
          });
        } else {
          // Retry with exponential backoff
          retryDelayRef.current = Math.min(retryDelayRef.current * 2, MAX_RETRY_MS);
          processingRef.current = false;
          if (navigator.onLine) {
            scheduleRetry(retryDelayRef.current);
          }
        }
      });

      return current;
    });
  }, [sendAlert]);

  const scheduleRetry = useCallback((delay: number) => {
    if (retryTimeoutRef.current !== null) {
      window.clearTimeout(retryTimeoutRef.current);
    }
    retryTimeoutRef.current = window.setTimeout(() => {
      retryTimeoutRef.current = null;
      processQueue();
    }, delay);
  }, [processQueue]);

  // When connectivity is restored, start processing the queue
  useEffect(() => {
    if (isOnline && pendingAlerts.length > 0 && !staleAlert) {
      retryDelayRef.current = BASE_RETRY_MS;
      processQueue();
    }
  }, [isOnline, pendingAlerts.length, staleAlert, processQueue]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current !== null) {
        window.clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const queueAlert = useCallback((data: CreateEmergencyAlertData) => {
    const alert: QueuedAlert = {
      id: `alert-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      data,
      queuedAt: Date.now(),
    };
    setPendingAlerts((prev) => [...prev, alert]);
  }, []);

  const confirmStaleAlert = useCallback(() => {
    if (!staleAlert) return;
    const alertToSend = staleAlert;
    setStaleAlert(null);

    // Send the stale alert immediately
    sendAlert(alertToSend).then((success) => {
      if (success) {
        setPendingAlerts((prev) => prev.filter((a) => a.id !== alertToSend.id));
        // Continue processing remaining
        retryDelayRef.current = BASE_RETRY_MS;
        setPendingAlerts((prev) => {
          if (prev.length > 0 && navigator.onLine) {
            scheduleRetry(BASE_RETRY_MS);
          }
          return prev;
        });
      } else {
        // Re-queue for retry
        retryDelayRef.current = BASE_RETRY_MS;
        if (navigator.onLine) {
          scheduleRetry(retryDelayRef.current);
        }
      }
    });
  }, [staleAlert, sendAlert, scheduleRetry]);

  const dismissStaleAlert = useCallback(() => {
    if (!staleAlert) return;
    const alertId = staleAlert.id;
    setStaleAlert(null);
    setPendingAlerts((prev) => {
      const remaining = prev.filter((a) => a.id !== alertId);
      // Continue processing remaining alerts
      if (remaining.length > 0 && navigator.onLine) {
        scheduleRetry(BASE_RETRY_MS);
      }
      return remaining;
    });
  }, [staleAlert, scheduleRetry]);

  return {
    queueAlert,
    pendingAlerts,
    staleAlert,
    confirmStaleAlert,
    dismissStaleAlert,
  };
}
